/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Plus, Filter, Download } from 'lucide-react';
import moment from 'moment';
import Card from '../ui/CardComponent';
import Button from '../ui/ButtonComponent';
import Badge from '../ui/BadgeComponent';
import { Link } from 'react-router-dom';
import FilterModal from '../components/filterModal';
import { PostAnalyticsProps, PostQueryParams, PostStatsProps } from '../../../../data/types/contentMarketing.interface';
import ContentMarketingService from '../../services/contentMarketing.services';
import LoadingBackdrop from '../../../shared/components/LoadingBackdrop';
import { Platforms } from '../../../shared/constants';

export default function CMDashboard() {
  const [isLoading, setIsLoading] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activePlatform, setActivePlatform] = useState('All');
  const [recentPosts, setRecentPosts] = useState<PostAnalyticsProps[]>([]);
  const [filters, setFilters] = useState<PostQueryParams>({
    perPage: 50,
    page: 1,
  });
  const [stats, setStats] = useState<PostStatsProps[]>([]);
  const [platforms] = useState(Platforms)

  const handleExport = () => {
    alert(
      'Export Data:\n\nThis would generate and download a report containing:\n• Performance metrics\n• Post analytics\n• Engagement statistics\n• Platform breakdowns\n\nFormats available: CSV, PDF, Excel'
    );
  };

  const getStats = async (activePlatform: string) => {
    try {
      setIsLoading(true);
      const response = await ContentMarketingService.getPlatformStats(activePlatform, 'dashboard', {
        platform: activePlatform,
      });
      setStats(response);
    } catch (error) {
      setStats([]);
      console.error('Error fetching stats:', error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  }

  const fetchPosts = async (props: PostQueryParams) => {
    try {
      setIsLoading(true);
      const response = await ContentMarketingService.getPlatformAnalytics(activePlatform, props);
      setIsLoading(false);
      setRecentPosts(response.data);
    } catch (error) {
      setRecentPosts([]);
      console.error('Error fetching filtered analytics:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getStats(activePlatform);
  }, [activePlatform]);

  useEffect(() => {
    fetchPosts(filters);
  }, [filters]);

  const handleFilterOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpenFilters(true);
  };

  const handleFilters = async (newFilters: PostQueryParams) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setOpenFilters(false);
  };

  const handleViewAll = () => {
    setFilters(prev => ({
      ...prev,
      perPage: 50,
      page: 1,
    }));
  };

  return (
    <div className="max-w-[1600px]">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-semibold text-[#111827]">Dashboard</h1>
          <div className="flex items-center gap-3">
            <Button variant="secondary" size="md" className="rounded" onClick={handleExport} hidden>
              <Download size={16} className="mr-2" />
              Export
            </Button>
            <Link to="/dashboard/content-marketing/media">
              <Button variant="primary" size="md" className="rounded">
                <Plus size={16} className="mr-2" />
                Create Post
              </Button>
            </Link>
          </div>
        </div>
        <p className="text-sm text-[#6B7280]">Overview of your content performance and scheduling</p>
      </div>

      {/* Platform Filters */}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <span className="text-sm text-[#6B7280] font-medium">Filter by Platform:</span>

        <div className="flex items-center gap-2">
          {platforms.map((platform) => {
            const isActive = activePlatform === platform.value;

            return (
              <button
                key={platform.id}
                type="button"
                onClick={() => {
                  setActivePlatform(platform.value as any)
                  handleFilters({
                    statuses: [platform.value],
                    perPage: 0,
                    page: 0
                  })
                }
                }
                className={`inline-flex items-center px-4 py-2 rounded-5 text-sm font-medium transition-all
            ${isActive
                    ? 'bg-[#F67570] text-white shadow-sm'
                    : 'bg-white text-[#6B7280] border border-[#E5E7EB] hover:bg-[#F9FAFB] hover:text-[#111827]'
                  }
          `}
              >
                {platform.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Stats Grid - 4 columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 mt-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label} padding="none">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-[#F9FAFB] border border-[#E5E7EB] rounded flex items-center justify-center">
                    <Icon size={20} className="text-[#6B7280]" />
                  </div>
                  <span className={`text-sm font-medium ${stat.trend === 'up' ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                    {stat.change}
                  </span>
                </div>
                <div className="text-3xl font-semibold text-[#111827] mb-1">{stat.value}</div>
                <div className="text-sm text-[#6B7280]">{stat.label}</div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid gap-6 mb-8">
        {/* Recent Posts - 2 columns */}
        <Card className="lg:col-span-2" padding="none">
          <div className="p-6 border-b border-[#E5E7EB]">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#111827]">Recent Posts</h2>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handleFilterOpen}>
                  <Filter size={16} className="mr-2" />
                  Filter
                </Button>
                <Link onClick={handleViewAll} to="/dashboard/content-marketing">
                  <Button variant="ghost" size="sm" hidden>
                    View All
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-[#1F2937] z-10">
                <tr className="bg-[#1F2937] text-white text-left text-xs font-medium uppercase tracking-wider">
                  <th className="px-6 py-3">Platform</th>
                  <th className="px-6 py-3">Title</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Date</th>
                  <th className="px-6 py-3">Engagement</th>
                  <th className="px-6 py-3">Reach</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E5E7EB] overflow-y-hidden">
                {recentPosts?.map((post) => (
                  <tr key={post.id} className="hover:bg-[#F9FAFB] transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant="default">{post.platform}</Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-[#111827] max-w-xs truncate">{post.title}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge
                        variant={
                          post.uploadStatus === 'Published' ? 'success' : post.uploadStatus === 'Scheduled' ? 'warning' : 'default'
                        }
                      >
                        {post.uploadStatus}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6B7280]">{post.scheduledAt ? moment(post?.scheduledAt).format('YYYY-MM-DD') : moment(post?.publishedAt).format('YYYY-MM-DD')}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#111827] font-medium">
                      {post.engagementRate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#111827] font-medium">{post.reach}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <LoadingBackdrop openBackdrop={isLoading} />
        </Card>

        {openFilters && (
          <FilterModal
            open={openFilters}
            anchorEl={anchorEl}
            filters={filters}
            handleClose={() => setOpenFilters(false)}
            handleFilters={handleFilters}
          />
        )}
      </div>
    </div>
  );
}
